/**
 * @module cercaliagl/service/Proximity
 */

import Service from './Service';
import Proj from '../proj';

/**
 * @typedef {Object} ProximityOptions
 * @property {string} [name] Name filter.
 * @property {string} [countryId] Country code filter.
 * @property {string} [country] Country name filter.
 * @property {string} [regionId] Region code filter.
 * @property {string} [region] Region name filter.
 * @property {string} [subregionId] Subregion code filter.
 * @property {string} [subregion] Subregion name filter.
 * @property {string} [subOrRegion] Region & Subregion name filter.
 * @property {string} [municipalityId] Municipality code filter.
 * @property {string} [municipality] Municipality name filter.
 * @property {string} [cityId] City code filter.
 * @property {string} [city] City name filter.
 * @property {string} [postalCode] Postal Code filter, requires country code.
 * @property {string} [streetId] Street code filter.
 * @property {string} [street] Street name filter.
 * @property {string} [street2] Second street name filter, for intersection search between main street and second street.
 * @property {string} [street2Id] Second street code filter, for intersection search between main street and second street.
 * @property {number} [housenumber] House number filter.
 * @property {string} [roadName] Road name filter.
 * @property {string} [roadId] Road code filter.
 * @property {number} [km] Mile marker filter (only for roads).
 * @property {string} [fullSearch] Resolves all the candidate parameters.
 * @property {string} [numCand] Maximum number of candidates per page.
 * @property {number} [posCand] First candidate position, starting from 0.
 * @property {number} [num] Maximum number of candidates.
 * @property {string} [wkt] Reference geometry.
 * @property {string} [srs] Coordinates reference system.
 * @property {number} [x] X coordinate central reference.
 * @property {number} [y] Y coordinate central reference.
 * @property {string} [rqge] Geo-entity level. Available values `adr|st|ct|mun|subreg|reg|ctry`.
 * @property {Array.<string>} [rqpoicats] Array of POIs categories.
 * @property {boolean} [infoxml] If a numeric value other than 0 is specified, it returns XML information associated with the Point of Interest.
 * @property {number} [weight] Proximity with real route distance. Values: `time`, `distance`, `money`.
 * @property {number} [inverse] Can be true or false. With a false value, the distances are calculated from the reference to the
 *  target. With a true value, the distances are calculated from the references to the targets. The default is false.
 * @property {Array.<string>} [mos] List of coordinates (type MO).
 * @property {number} [rad] Maximum search radius (direct distance).
 */

/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const proximityService = new cercaliagl.service.Proximity({ ... });
 * ```
 *
 * The Proximity service offers to obtain data given a reference and a distance or geometry.
 * For example, given a geometry it is possible to obtain al geo entities inside it. Also it can return POIs.<br/>
 * All options available below in constructor options.<br/>
 *
 * Example usage:
 *
 * ```js
 * // Obtain localities (ct) inside 5km radius given center Madrid municipality. 100 candidates maximum.
 * const proximityService = new cercaliagl.service.Proximity({
 *   rqge: 'ct',
 *   countryId: 'ESP',
 *   municipality: 'Madrid',
 *   rad: 5000,
 *   numCand: 100
 * });
 * proximityService.proximity((data) => console.log(data));
 * ```
 *
 * @extends {Service}
 * @api
 */
class Proximity extends Service {
  /**
   * @param {ProximityOptions} [options] Proximity options
   */
  constructor(options = {}) {
    super();

    /**
     * @type {Object}
     * @private
     */
    this.response_ = null;

    /**
     * Search by name, general search with no filters (locn)
     * @type {string|null}
     */
    this.name_ = options.name ? options.name : null;

    /**
     * Country code filter (ctryc)
     * @type {string|null}
     */
    this.countryId_ = options.countryId ? options.countryId : null;

    /**
     * Country name filter (ctryn)
     * @type {string|null}
     */
    this.country_ = options.country ? options.country : null;

    /**
     * Region code filter (regc)
     * @type {string|null}
     */
    this.regionId_ = options.regionId ? options.regionId : null;

    /**
     * Region name filter (regn)
     * @type {string|null}
     */
    this.region_ = options.region ? options.region : null;

    /**
     * Subregion code filter (subregc)
     * @type {string|null}
     */
    this.subregionId_ = options.subregionId ? options.subregionId : null;

    /**
     * Subregion name filter (subregn)
     * @type {string|null}
     */
    this.subregion_ = options.subregion ? options.subregion : null;

    /**
     * Region / subregion name filter (rsn)
     * @type {string|null}
     */
    this.subOrRegion_ = options.subOrRegion ? options.subOrRegion : null;

    /**
     * Municipality code filter (munc)
     * @type {string|null}
     */
    this.municipalityId_ = options.municipalityId ? options.municipalityId : null;

    /**
     * Municipality name filter (munn)
     * @type {string|null}
     */
    this.municipality_ = options.municipality ? options.municipality : null;

    /**
     * City code filter (ctc)
     * @type {string|null}
     */
    this.cityId_ = options.cityId ? options.cityId : null;

    /**
     * City name filter (ctn)
     * @type {string|null}
     */
    this.city_ = options.city ? options.city : null;

    /**
     * Postal code filter, requires country code (pcode)
     * @type {string|null}
     */
    this.postalCode_ = options.postalCode ? options.postalCode : null;

    /**
     * Street code filter (stc)
     * @type {string|null}
     */
    this.streetId_ = options.streetId ? options.streetId : null;

    /**
     * Street name filter (stn)
     * @type {string|null}
     */
    this.street_ = options.street ? options.street : null;

    /**
     * Second street name filter, for street intersection seach (istn)
     * @type {string|null}
     */
    this.street2_ = options.street2 ? options.street2 : null;

    /**
     * Second street code filter, for street intersection seach  (istc)
     * @type {string|null}
     */
    this.street2Id_ = options.street2Id ? options.street2Id : null;

    /**
     * Housenumber filter (stnum)
     * @type {number|null}
     */
    this.housenumber_ = options.housenumber ? options.housenumber : null;

    /**
     * Road name filter (rdn)
     * @type {string|null}
     */
    this.roadName_ = options.roadName ? options.roadName : null;

    /**
     * Road cod filter (rdc)
     * @type {string|null}
     */
    this.roadId_ = options.roadId ? options.roadId : null;

    /**
     * Road milestone filter (km)
     * @type {number|null}
     */
    this.km_ = options.km ? options.km : null;

    /**
     * Solve all the candidate params (fullsearch)
     * @type {string|null}
     */
    this.fullSearch_ = options.fullSearch ? options.fullSearch : null;

    /**
     * Maximum number of candidates per page (numcand)
     * @type {string|null}
     */
    this.numCand_ = options.numCand ? options.numCand : null;

    /**
     * First candidate position for every page, starting from 0 (poscand)
     * @type {number|null}
     */
    this.posCand_ = options.posCand ? options.posCand : null;

    /**
     * Maximum number of candidates. Maxim server value 99 (num)
     * @type {number|null}
     */
    this.num_ = options.num ? options.num : null;

    /**
     *
     * Geometry.
     * Required parameters:
     *  - wkt
     *  - srs (default: EPSG:4326)
     */

    /**
     * @type {string|null}
     */
    this.wkt_ = options.wkt ? options.wkt : null;

    /**
     * @type {string}
     */
    this.srs_ = options.srs ? options.srs : Proj.EPSG4326;

    /**
     *
     * Coordinate.
     * Required parameters:
     *  - x
     *  - y
     *  - srs (por defecto: EPSG:4326)
     */

    /**
     * @type {number|null}
     */
    this.x_ = options.x ? options.x : null;

    /**
     * @type {number|null}
     */
    this.y_ = options.y ? options.y : null;

    /**
     * @type {string|null}
     */
    this.rqge_ = options.rqge ? options.rqge : null;

    /**
     * @type {Array.<string>|null}
     */
    this.rqpoicats_ = options.rqpoicats ? options.rqpoicats : null;

    /**
     * @type {boolean|null}
     */
    this.infoxml_ = options.infoxml ? true : null;

    /**
     * @type {number|null}
     */
    this.weight_ = options.weight ? options.weight : null;

    /**
     * @type {boolean|null}
     */
    this.inverse_ = options.inverse ? true : null;

    /**
     * @type {Array.<string>|null}
     */
    this.mos_ = options.mos ? options.mos : null;

    /**
     * @type {number|null}
     */
    this.rad_ = options.rad ? options.rad : null;
  }

  /**
   * Clear search
   * @api
   */
  clear() {
    this.name_ = null;
    this.countryId_ = null;
    this.country_ = null;
    this.regionId_ = null;
    this.region_ = null;
    this.subregionId_ = null;
    this.subregion_ = null;
    this.subOrRegion_ = null;
    this.municipalityId_ = null;
    this.municipality_ = null;
    this.cityId_ = null;
    this.city_ = null;
    this.postalCode_ = null;
    this.streetId_ = null;
    this.street_ = null;
    this.street2_ = null;
    this.street2Id_ = null;
    this.housenumber_ = null;
    this.roadName_ = null;
    this.roadId_ = null;
    this.km_ = null;
    this.fullSearch_ = null;
    this.numCand_ = null;
    this.posCand_ = null;
    this.num_ = null;
    this.rqge_ = null;
    this.rqpoicats_ = null;
    this.infoxml_ = null;
    this.weight_ = null;
    this.inverse_ = null;
    this.mos_ = null;
    this.rad_ = null;

    this.response_ = null;
  }

  /**
   * Set search. Same parameters as constructor
   * @param {ProximityOptions} options Proximity search options.
   * @api
   */
  setSearch(options) {
    this.name_ = options.name ? options.name : null;
    this.countryId_ = options.countryId ? options.countryId : null;
    this.country_ = options.country ? options.country : null;
    this.regionId_ = options.regionId ? options.regionId : null;
    this.region_ = options.region ? options.region : null;
    this.subregionId_ = options.subregionId ? options.subregionId : null;
    this.subregion_ = options.subregion ? options.subregion : null;
    this.subOrRegion_ = options.subOrRegion ? options.subOrRegion : null;
    this.municipalityId_ = options.municipalityId ? options.municipalityId : null;
    this.municipality_ = options.municipality ? options.municipality : null;
    this.cityId_ = options.cityId ? options.cityId : null;
    this.city_ = options.city ? options.city : null;
    this.postalCode_ = options.postalCode ? options.postalCode : null;
    this.streetId_ = options.streetId ? options.streetId : null;
    this.street_ = options.street ? options.street : null;
    this.street2_ = options.street2 ? options.street2 : null;
    this.street2Id_ = options.street2Id ? options.street2Id : null;
    this.housenumber_ = options.housenumber ? options.housenumber : null;
    this.roadName_ = options.roadName ? options.roadName : null;
    this.roadId_ = options.roadId ? options.roadId : null;
    this.km_ = options.km ? options.km : null;
    this.fullSearch_ = options.fullSearch ? options.fullSearch : null;
    this.numCand_ = options.numCand ? options.numCand : null;
    this.posCand_ = options.posCand ? options.posCand : null;
    this.num_ = options.num ? options.num : null;

    this.wkt_ = options.wkt ? options.wkt : null;
    this.srs_ = options.srs ? options.srs : Proj.EPSG4326; // default

    this.x_ = options.x ? options.x : null;
    this.y_ = options.y ? options.y : null;

    this.rqge_ = options.rqge ? options.rqge : null;
    this.rqpoicats_ = options.rqpoicats ? options.rqpoicats : null;
    this.infoxml_ = options.infoxml ? options.infoxml : null;
    this.weight_ = options.weight ? options.weight : null;
    this.inverse_ = !!options.inverse; // default
    this.mos_ = options.mos ? options.mos : null;
    this.rad_ = options.rad ? options.rad : null;
    this.response_ = null;
  }

  /**
   * Returns response to the query with function 'proximity'
   * @return {Object} response
   * @api
   */
  getResponse() {
    return this.response_;
  }

  /**
   * Make request and obtain data via callback function.
   * @param {Function} callbackFn Callback Function
   * @param {Function} callbackFnError Callback Function if there is an error
   * @api
   */
  proximity(callbackFn, callbackFnError) {
    const params = {
      cmd: 'proximity',
    };

    if (this.name_) {
      params.name = this.name_;
    }
    if (this.countryId_) {
      params.countryId = this.countryId_;
    }
    if (this.country_) {
      params.country = this.country_;
    }
    if (this.regionId_) {
      params.regionId = this.regionId_;
    }
    if (this.region_) {
      params.region = this.region_;
    }
    if (this.subregionId_) {
      params.subregionId = this.subregionId_;
    }
    if (this.subregion_) {
      params.subregion = this.subregion_;
    }
    if (this.subOrRegion_) {
      params.subOrRegion = this.subOrRegion_;
    }
    if (this.municipalityId_) {
      params.municipalityId = this.municipalityId_;
    }
    if (this.municipality_) {
      params.municipality = this.municipality_;
    }
    if (this.cityId_) {
      params.cityId = this.cityId_;
    }
    if (this.city_) {
      params.city = this.city_;
    }
    if (this.postalCode_) {
      params.postalCode = this.postalCode_;
    }
    if (this.streetId_) {
      params.streetId = this.streetId_;
    }
    if (this.street_) {
      params.street = this.street_;
    }
    if (this.street2_) {
      params.street2 = this.street2_;
    }
    if (this.street2Id_) {
      params.street2Id = this.street2Id_;
    }
    if (this.housenumber_) {
      params.housenumber = this.housenumber_;
    }
    if (this.roadName_) {
      params.roadName = this.roadName_;
    }
    if (this.roadId_) {
      params.roadId = this.roadId_;
    }
    if (this.km_) {
      params.km = this.km_;
    }
    if (this.fullSearch_) {
      params.fullSearch = this.fullSearch_;
    }
    if (this.numCand_) {
      params.numCand = this.numCand_;
    }
    if (this.posCand_) {
      params.posCand = this.posCand_;
    }
    if (this.num_) {
      params.num = this.num_;
    }
    if (this.wkt_) {
      params.wkt = this.wkt_;
    }
    if (this.srs_) {
      params.srs = this.srs_;
    }
    if (this.x_) {
      params.x = this.x_;
    }
    if (this.y_) {
      params.y = this.y_;
    }
    if (this.rqge_) {
      params.rqge = this.rqge_;
    }
    if (this.rqpoicats_) {
      params.rqpoicats = this.rqpoicats_.join(',');
    }
    if (this.infoxml_) {
      params.infoxml = this.infoxml_ ? 1 : 0;
    }
    if (this.weight_) {
      params.weight = this.weight_;
    }
    if (this.inverse_) {
      params.inverse = this.inverse_ ? 1 : 0;
    }
    if (this.mos_) {
      params.mos = this.mos_.join(',');
    }
    if (this.rad_) {
      params.rad = this.rad_;
    }

    const reqParams = {
      timeout: this.wkt_ ? 50000 : undefined,
      data: params,
      callbackFn: (data) => {
        this.response_ = data;
        if (callbackFn) {
          callbackFn(this.response_);
        } else {
          console.warn('No callbackfunction defined'); // eslint-disable-line no-console
        }
      },
      callbackFnError: (error) => {
        if (callbackFnError) {
          callbackFnError(error);
        }
      },
    };

    this.getRequest(reqParams);
  }
}

export default Proximity;
